import CloseIcon from '@mui/icons-material/Close';
import React from 'react';
import type { MasterLabelReviewIssueType } from '../../types';

export const REVIEW_ISSUE_CHOOSER_OPTIONS: Array<{
  key: string;
  value: MasterLabelReviewIssueType;
  label: string;
}> = [
  { key: 'D', value: 'missing_design', label: 'Missing design' },
  { key: 'S', value: 'sop_unclear', label: 'SOP unclear' },
  { key: 'B', value: 'bad_quality', label: 'Bad quality' },
  { key: 'M', value: 'mark_for_deletion', label: 'Mark for deletion' },
  { key: 'O', value: 'other', label: 'Other' },
];

export type ReviewIssueShortcutAction = MasterLabelReviewIssueType | 'clear';

export function reviewIssueActionFromShortcutKey(key: string): ReviewIssueShortcutAction | null {
  const normalizedKey = key.toUpperCase();
  if (normalizedKey === 'C') return 'clear';
  return REVIEW_ISSUE_CHOOSER_OPTIONS.find((option) => option.key === normalizedKey)?.value ?? null;
}

export const ReviewIssueChooserOverlay: React.FC<{
  onSelect: (value: MasterLabelReviewIssueType) => void;
  onClear: () => void;
  onClose: () => void;
}> = ({ onSelect, onClear, onClose }) => (
  <div
    style={{
      position: 'fixed',
      inset: 0,
      background: 'rgba(15, 23, 42, 0.36)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 30,
    }}
    onClick={onClose}
  >
    <div
      onClick={(event) => event.stopPropagation()}
      style={{
        width: 360,
        maxWidth: 'calc(100vw - 32px)',
        background: 'var(--surface)',
        border: '1px solid var(--border)',
        borderRadius: 'var(--radius-md)',
        boxShadow: '0 14px 32px rgba(15, 23, 42, 0.22)',
        padding: 14,
        display: 'flex',
        flexDirection: 'column',
        gap: 10,
      }}
    >
      <div
        style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}
      >
        <div>
          <div style={{ fontFamily: 'var(--font-mono)', fontSize: 12, fontWeight: 700 }}>
            Review Issue
          </div>
          <div style={{ fontSize: 11, color: 'var(--text-tertiary)', marginTop: 2 }}>
            Choose a review marker for the selected crops.
          </div>
        </div>
        <button
          type="button"
          aria-label="Close review issue chooser"
          onClick={onClose}
          style={{
            border: 'none',
            background: 'transparent',
            padding: 0,
            display: 'inline-flex',
            alignItems: 'center',
            color: 'var(--text-tertiary)',
            cursor: 'pointer',
          }}
        >
          <CloseIcon style={{ fontSize: 14 }} />
        </button>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {REVIEW_ISSUE_CHOOSER_OPTIONS.map((option) => (
          <button
            key={option.value}
            data-testid={`review-chooser-option-${option.value}`}
            onClick={() => onSelect(option.value)}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 8,
              padding: '8px 10px',
              borderRadius: 'var(--radius-sm)',
              border: '1px solid var(--border)',
              background: 'var(--bg)',
              color: 'var(--text-primary)',
              cursor: 'pointer',
              fontSize: 11,
              fontWeight: 600,
            }}
          >
            <span>{option.label}</span>
            <span style={{ fontFamily: 'var(--font-mono)', color: 'var(--text-tertiary)' }}>
              {option.key}
            </span>
          </button>
        ))}
        <div style={{ height: 1, background: 'var(--border)', margin: '2px 0' }} />
        <button
          data-testid="review-chooser-clear"
          onClick={onClear}
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 8,
            padding: '8px 10px',
            borderRadius: 'var(--radius-sm)',
            border: '1px solid rgba(239, 68, 68, 0.24)',
            background: 'rgba(239, 68, 68, 0.08)',
            color: 'var(--red)',
            cursor: 'pointer',
            fontSize: 11,
            fontWeight: 600,
          }}
        >
          <span>Clear review issue</span>
          <span style={{ fontFamily: 'var(--font-mono)', color: 'var(--text-tertiary)' }}>C</span>
        </button>
      </div>
      <div style={{ fontSize: 10, color: 'var(--text-tertiary)', fontFamily: 'var(--font-mono)' }}>
        Esc closes this window.
      </div>
    </div>
  </div>
);
